<template>
  <b-modal
    id="add-entry-modal"
    modal-class="timesheet-modal"
    size="lg"
    title="Add Timesheet Entry"
    :ok-title="okTitle"
    :ok-disabled="!canSubmit || submitting"
    :busy="submitting"
    :no-close-on-backdrop="submitting"
    :hide-header-close="submitting"
    @ok="$emit('ok', $event)"
    @hidden="$emit('hidden')"
  >
    <div class="entry-head">
      <b-form-group label="Tradie">
        <b-form-select
          v-model="form.tradie_id"
          :options="tradieOptions"
          :disabled="!!form._locked_tradie"
        />
      </b-form-group>

      <!-- Entry type drives the shape of everything below it, so it is shown
           as segments rather than hidden behind a dropdown. Worked is the
           default and serializes as null, matching LEAVE_TYPE_OPTIONS. -->
      <b-form-group label="Entry Type">
        <div class="entry-seg" role="group">
          <button
            v-for="opt in leaveTypeOptions"
            :key="opt.value || 'worked'"
            type="button"
            class="entry-seg-btn"
            :class="{ active: form.leave_type === opt.value }"
            @click="selectLeaveType(opt.value)"
          >
            {{ opt.text }}
          </button>
        </div>
      </b-form-group>

      <b-form-group label="Project" class="mb-0">
        <b-form-select v-model="form.project_id" :options="projectOptions" />
      </b-form-group>
    </div>

    <!-- ── Worked: clock in / clock out ─────────────────────────────────── -->
    <div v-if="!isLeave" class="d-flex entry-cards">
      <div class="entry-card entry-card--in">
        <div class="entry-card-head">
          <span class="entry-icon entry-icon--in">
            <i class="far fa-clock" />
          </span>
          <span class="entry-card-title">Clock in</span>
        </div>
        <p class="entry-card-sub">When the shift started</p>
        <b-form-group :label="isMultiDay ? 'Start date' : 'Date'">
          <b-form-datepicker
            v-model="form.in_date"
            :max="maxFutureDate"
            boundary="window"
            locale="en-AU"
            today-button
            reset-button
            close-button
            show-decade-nav
          />
        </b-form-group>
        <b-form-group :label="isMultiDay ? 'Start time' : 'Time'" class="mb-0">
          <datetime-input v-model="form.in_time" type="time" />
        </b-form-group>
      </div>

      <div class="entry-card">
        <div class="entry-card-head">
          <span class="entry-icon entry-icon--out">
            <i class="far fa-clock" />
          </span>
          <span class="entry-card-title">Clock out</span>
          <small v-if="!isMultiDay" class="entry-optional">OPTIONAL</small>
        </div>
        <p class="entry-card-sub">
          {{
            isMultiDay
              ? 'Applied to every day in the range'
              : 'Leave blank for an open shift'
          }}
        </p>
        <!-- Across a range the finish is a time-of-day on each day, so a
             single out-date would be meaningless. -->
        <b-form-group v-if="!isMultiDay" label="Date">
          <b-form-datepicker
            v-model="form.out_date"
            :min="form.in_date || undefined"
            :max="maxFutureDate"
            boundary="window"
            locale="en-AU"
            today-button
            reset-button
            close-button
            show-decade-nav
          />
        </b-form-group>
        <b-form-group
          :label="isMultiDay ? 'Finish time' : 'Time'"
          :class="showBreak ? '' : 'mb-0'"
        >
          <datetime-input
            v-model="form.out_time"
            type="time"
            :disabled="!isMultiDay && !form.out_date"
          />
        </b-form-group>
        <!-- Break belongs to the shift, so it lives with the clock-out that
             defines it — and it fills the space the out-date leaves when a
             range hides it. -->
        <b-form-group v-if="showBreak" label="Break (minutes)" class="mb-0">
          <b-form-input
            v-model.number="form.break_minutes"
            type="number"
            min="0"
          />
        </b-form-group>
      </div>
    </div>

    <!-- ── Leave: no clock times exist for these days ───────────────────── -->
    <div v-else class="entry-card entry-card--leave">
      <div class="entry-card-head">
        <span class="entry-icon entry-icon--out">
          <i class="far fa-calendar" />
        </span>
        <span class="entry-card-title">{{ leaveLabel }}</span>
      </div>
      <p class="entry-card-sub">
        {{ leaveLabel }} days don't record clock in or clock out times.
      </p>
      <div class="d-flex" style="gap: 12px">
        <b-form-group
          :label="isMultiDay ? 'Start date' : 'Date'"
          class="flex-fill mb-0"
        >
          <b-form-datepicker
            v-model="form.in_date"
            :max="maxFutureDate"
            boundary="window"
            locale="en-AU"
            today-button
            reset-button
            close-button
            show-decade-nav
          />
        </b-form-group>
        <b-form-group label="Hours per day" class="flex-fill mb-0">
          <b-form-input
            v-model.number="form.leave_hours"
            type="number"
            min="0"
            max="24"
            step="0.5"
            placeholder="Company default"
          />
          <small class="entry-hint">
            Leave blank to use the company default for this leave type.
          </small>
        </b-form-group>
      </div>
    </div>

    <!-- ── Multiple days (replaces the old separate bulk modal) ─────────── -->
    <!-- Given the same head/sub anatomy as the clock cards so the range stops
         reading as a stray checkbox between two card groups. -->
    <div
      class="entry-card entry-multi"
      :class="{ 'entry-multi--off': !isMultiDay }"
    >
      <div class="entry-card-head entry-multi-head">
        <span class="entry-icon entry-icon--range">
          <i class="far fa-calendar-alt" />
        </span>
        <span class="entry-multi-text">
          <span class="entry-card-title">Multiple days</span>
          <span class="entry-card-sub mb-0">
            {{
              isLeave
                ? 'Record this leave across a date range'
                : 'Repeat these times across a date range'
            }}
          </span>
        </span>
        <b-form-checkbox
          v-model="form.multi_day"
          switch
          class="entry-multi-sw"
        />
      </div>
      <div v-if="isMultiDay" class="entry-multi-body">
        <div class="d-flex" style="gap: 12px">
          <b-form-group label="End date" class="flex-fill mb-0">
            <b-form-datepicker
              v-model="form.end_date"
              :min="form.in_date || undefined"
              :max="maxFutureDate"
              boundary="window"
              locale="en-AU"
              today-button
              reset-button
              close-button
              show-decade-nav
            />
          </b-form-group>
          <!-- Same segment language as Entry Type. As a labelled field it has
               a baseline to align to, and "Weekdays" states the default rather
               than leaving it to be inferred from an unticked box. -->
          <b-form-group label="Days included" class="flex-fill mb-0">
            <div class="entry-seg entry-seg--pair" role="group">
              <button
                type="button"
                class="entry-seg-btn"
                :class="{ active: !form.include_weekends }"
                @click="form.include_weekends = false"
              >
                Weekdays
              </button>
              <button
                type="button"
                class="entry-seg-btn"
                :class="{ active: form.include_weekends }"
                @click="form.include_weekends = true"
              >
                All days
              </button>
            </div>
          </b-form-group>
        </div>
        <p v-if="dayCount" class="entry-summary">
          <strong>{{ dayCount }} day{{ dayCount === 1 ? '' : 's' }}</strong>
          will be added{{ isLeave ? '.' : ', each with the same times.' }}
          <template v-if="weekendDaysSkipped">
            {{ weekendDaysSkipped }} weekend day{{
              weekendDaysSkipped === 1 ? '' : 's'
            }}
            skipped.
          </template>
        </p>
      </div>
    </div>

    <!-- ── Equipment used: only meaningful once the shift has a clock-out,
         since plant usage attaches to a completed event server-side. Not
         offered for multi-day entries — one call to the bulk endpoint covers
         every day in the range, so there is no single day for a quantity to
         attach to. -->
    <div v-if="showPlantSection" class="plant-section">
      <div class="plant-section-head">
        <span class="modal-section-label mb-0">Equipment used</span>
        <button type="button" class="plant-add-link" @click="addPlantRow">
          + Add item
        </button>
      </div>
      <plant-entry-row
        v-for="(entry, index) in form.plant_entries"
        :key="index"
        :value="entry"
        :equipment-options="equipmentOptions"
        @input="updatePlantRow(index, $event)"
        @remove="removePlantRow(index)"
      />
      <p v-if="!form.plant_entries.length" class="plant-empty">
        No equipment logged for this shift.
      </p>
    </div>

    <hr class="my-3" />
    <b-form-group :label="isLeave ? 'Note (optional)' : 'Comment (optional)'">
      <b-form-input v-model="form.comment" />
    </b-form-group>
  </b-modal>
</template>

<script>
import DatetimeInput from '@/components/timesheets/DatetimeInput.vue'
import PlantEntryRow from '@/components/timesheets/shared/PlantEntryRow.vue'
import {
  LEAVE_TYPE_OPTIONS,
  leaveTypeLabel,
  buildEquipmentOptions,
} from '@/utils/timesheetHelpers'

export default {
  name: 'TimesheetAddEntryModal',
  components: { DatetimeInput, PlantEntryRow },
  props: {
    form: { type: Object, required: true },
    tradieOptions: { type: Array, default: () => [] },
    projectOptions: { type: Array, default: () => [] },
    today: { type: String, default: '' },
    maxFutureDate: { type: String, default: '' },
    submitting: { type: Boolean, default: false },
  },
  created() {
    // form is shared/mutated in place by callers (see selectLeaveType
    // below), and older callers predate this field — default it defensively
    // rather than assuming every caller was updated.
    if (!this.form.plant_entries) {
      this.$set(this.form, 'plant_entries', [])
    }
  },
  data() {
    return {
      equipmentItems: [],
    }
  },
  computed: {
    leaveTypeOptions() {
      return LEAVE_TYPE_OPTIONS
    },
    isLeave() {
      return !!this.form.leave_type
    },
    isMultiDay() {
      return !!this.form.multi_day
    },
    leaveLabel() {
      return leaveTypeLabel(this.form.leave_type) || 'Leave'
    },
    okTitle() {
      return this.isMultiDay ? 'Add Entries' : 'Add Entry'
    },
    showBreak() {
      return !this.isLeave && (this.isMultiDay || !!this.form.out_date)
    },
    // Inclusive walk of the range, split into what will be created and what
    // the weekend rule drops, so the operator sees both before they create
    // anything.
    rangeDays() {
      const empty = { included: 0, weekendsSkipped: 0 }
      if (!this.isMultiDay || !this.form.in_date || !this.form.end_date) {
        return empty
      }
      const start = new Date(`${this.form.in_date}T00:00:00`)
      const end = new Date(`${this.form.end_date}T00:00:00`)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return empty
      }
      if (end < start) return empty
      const spanDays = Math.round((end - start) / 86400000) + 1 // inclusive of both ends
      let included = 0
      let weekendsSkipped = 0
      for (let i = 0; i < spanDays; i += 1) {
        const day = new Date(start)
        day.setDate(day.getDate() + i)
        const dow = day.getDay()
        const isWeekend = dow === 0 || dow === 6
        if (this.form.include_weekends || !isWeekend) {
          included += 1
        } else {
          weekendsSkipped += 1
        }
      }
      return { included, weekendsSkipped }
    },
    dayCount() {
      return this.rangeDays.included
    },
    weekendDaysSkipped() {
      return this.rangeDays.weekendsSkipped
    },
    showPlantSection() {
      return (
        !this.isLeave &&
        !this.isMultiDay &&
        !!this.form.out_date &&
        !!this.form.out_time
      )
    },
    equipmentOptions() {
      return buildEquipmentOptions(this.equipmentItems)
    },
    canSubmit() {
      if (!this.form.tradie_id || !this.form.project_id || !this.form.in_date) {
        return false
      }
      if (this.isMultiDay) {
        if (!this.form.end_date) return false
        // A range that excludes every day in it would silently create nothing.
        if (!this.dayCount) return false
        // The bulk worked endpoint requires both ends of the shift.
        if (!this.isLeave && (!this.form.in_time || !this.form.out_time)) {
          return false
        }
      } else if (!this.isLeave && !this.form.in_time) {
        return false
      }
      // A row half-filled (item picked, no quantity yet, or vice versa)
      // shouldn't submit silently as a dropped row — make the user finish
      // or remove it.
      if (
        this.showPlantSection &&
        this.form.plant_entries.some(
          (e) => !e.plant_equipment_id !== !e.quantity,
        )
      ) {
        return false
      }
      return true
    },
  },
  watch: {
    'form.project_id': {
      immediate: true,
      handler(projectId) {
        this.fetchEquipmentOptions(projectId)
      },
    },
    // Moving the start date past the current end date left an invalid
    // range (end < start) sitting silently in the form. Snap end forward to
    // follow start, matching how the picker's own :min bound already stops
    // a user from doing this by hand — this covers the today-button/reset
    // paths, which bypass :min.
    'form.in_date'(newStart) {
      if (this.form.end_date && newStart && this.form.end_date < newStart) {
        this.form.end_date = newStart
      }
    },
    showPlantSection(visible) {
      // The clock-out step that unlocks this section is also the one that
      // can be cleared again (switching entry type, undoing the out time) —
      // drop any picked equipment so a hidden section can never submit stale
      // rows the user no longer sees.
      if (!visible && this.form.plant_entries.length) {
        this.form.plant_entries = []
      }
    },
  },
  methods: {
    selectLeaveType(value) {
      this.form.leave_type = value
      // Clock fields and leave hours are mutually exclusive. Clear the side
      // that just became unreachable so a hidden value can never be submitted.
      if (value) {
        this.form.out_date = ''
        this.form.out_time = ''
        this.form.break_minutes = 0
      } else {
        this.form.leave_hours = null
      }
    },
    async fetchEquipmentOptions(projectId) {
      const params = projectId ? { project_id: projectId } : {}
      try {
        await this.$store.dispatch('plantEquipment/getPlantEquipment', params)
        this.equipmentItems = this.$store.getters['plantEquipment/items']
      } catch (e) {
        this.equipmentItems = []
      }
    },
    addPlantRow() {
      this.form.plant_entries.push({ plant_equipment_id: null, quantity: 1 })
    },
    updatePlantRow(index, value) {
      this.form.plant_entries.splice(index, 1, value)
    },
    removePlantRow(index) {
      this.form.plant_entries.splice(index, 1)
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-section-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 10px;
}

.entry-head {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  margin: 0 0 16px;
}

.entry-seg {
  display: flex;
  border: 1px solid #ced4da;
  border-radius: 5px;
  overflow: hidden;
}

.entry-seg-btn {
  flex: 1;
  border: 0;
  border-right: 1px solid #ced4da;
  background: #fff;
  padding: 9px 4px;
  font-size: 12.5px;
  color: #495057;
  font-family: inherit;
  line-height: 1.2;

  &:last-child {
    border-right: 0;
  }

  &.active {
    background: #006cff;
    color: #fff;
    font-weight: 600;
  }
}

.entry-cards {
  gap: 14px;
  margin: 0 0 16px;
}

.entry-card {
  flex: 1;
  min-width: 0;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 14px;
  background: #fff;

  &--in {
    background: #f6fbf8;
    border-color: #cde9da;
  }

  &--leave {
    margin: 0 0 16px;
  }
}

.entry-card-head {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0 0 3px;
}

.entry-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex: 0 0 auto;

  &--in {
    background: #d9f2e5;
    color: #198754;
  }

  &--out {
    background: #dbeafe;
    color: #006cff;
  }

  &--range {
    background: #ede9fe;
    color: #6d4aff;
  }
}

.entry-card-title {
  font-weight: 600;
}

.entry-optional {
  font-size: 10.5px;
  font-weight: 700;
  color: #adb5bd;
  letter-spacing: 0.05em;
}

.entry-card-sub {
  font-size: 12.5px;
  color: #6b7280;
  margin: 0 0 11px;
}

.entry-hint {
  display: block;
  font-size: 11.5px;
  color: #6b7280;
  margin: 4px 0 0;
  line-height: 1.3;
}

// The range is a card like the clock cards, but it is a section rather than a
// column, so it carries its own padding on the head and body instead of the
// card-level padding the flex columns use.
.entry-multi {
  flex: none;
  margin: 0 0 14px;
  padding: 0;

  &--off {
    background: #fbfbfc;
  }
}

.entry-multi-head {
  margin: 0;
  padding: 13px 14px;
}

.entry-multi-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

// Pushes the switch to the far end of the head row, where it reads as the
// control that arms the section rather than a checkbox in a list.
.entry-multi-sw {
  width: 56px;
  height: 32px;
  min-height: 32px;
  padding-left: 0;
  margin-left: auto;
  flex: 0 0 56px;

  // main.scss enlarges every switch without fully replacing Bootstrap's
  // original offsets. Give this empty-label switch a complete local layout so
  // the thumb stays inside its track in both states.
  ::v-deep .custom-control-label {
    display: block;
    width: 56px;
    height: 32px;
    padding-top: 0;

    &::before {
      top: 0;
      left: 0;
      width: 56px !important;
      height: 32px !important;
      border-radius: 16px !important;
      transform: none;
      transition: background-color 0.2s ease, border-color 0.2s ease;
    }

    &::after {
      top: 2px;
      left: 2px;
      width: 28px !important;
      height: 28px !important;
      border-radius: 50% !important;
      transform: none;
      transition: transform 0.2s ease;
    }
  }

  // Tradiepass blue on, matching the checked color already used for
  // checkboxes/radios (#006CFF), instead of the shared green switch color.
  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    border-color: #006cff;
    background-color: #006cff;
  }

  ::v-deep .custom-control-input:checked ~ .custom-control-label::after {
    left: 2px;
    transform: translateX(24px);
  }
}

.entry-multi-body {
  margin: 0;
  padding: 14px;
  border-top: 1px solid #e9ecef;
}

// Two-up variant of the Entry Type strip: fewer, wider segments, so the labels
// do not look stranded in a half-empty button.
.entry-seg--pair .entry-seg-btn {
  padding: 10px 6px;
  font-size: 13px;
}

.entry-summary {
  font-size: 12.5px;
  color: #6b7280;
  margin: 10px 0 0;
  line-height: 1.5;
}

.plant-section {
  background: #fbf4e8;
  border: 1px solid #ecd3a0;
  border-radius: 8px;
  padding: 14px;
  margin: 0 0 16px;
}

.plant-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 10px;
}

.plant-add-link {
  border: 0;
  background: none;
  padding: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: #006cff;
}

.plant-empty {
  font-size: 12.5px;
  color: #9c8355;
  margin: 0;
}
</style>
